import { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabase'

const PRIORITY_COLORS = { High: '#dc3545', Medium: '#e6a817', Low: '#1e3d30' }
const STATUS_COLORS = {
  'New Lead': '#6b8278', 'In Progress': '#e6a817', 'Quote Sent': '#3b82f6',
  'Awaiting Client': '#e6a817', 'Under Referral': '#8b5cf6',
  'No Contact 1': '#f97316', 'No Contact 2': '#ea580c', 'No Contact 3': '#c2410c',
  'Policy Placed': '#1e3d30', 'Not Interested': '#dc3545',
}
const STATUS_ORDER = [
  'New Lead', 'In Progress', 'Quote Sent', 'Awaiting Client',
  'Under Referral', 'No Contact 1', 'No Contact 2', 'No Contact 3',
]
const BROKERS = ['Ben Rogers', 'Jesse Roach', 'Mitch Speer']
const BROKER_PHOTOS = {
  'Jesse Roach': 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0061_VSCO.jpg',
  'Ben Rogers':  'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0013_VSCO.jpg',
  'Mitch Speer': 'https://dtdwmzviywqumpouqula.supabase.co/storage/v1/object/public/assets/DSC_0023_VSCO.jpg',
}

const daysSince = (d) => { if (!d) return null; return Math.floor((new Date() - new Date(d)) / 86400000) }
const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000) }
const fmtTimeAgo = (d) => {
  if (!d) return '—'
  const mins = Math.floor((new Date() - new Date(d)) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, value, sub, color, onClick, urgent }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface)',
        border: `1.5px solid ${urgent ? 'rgba(220,53,69,0.3)' : 'var(--border)'}`,
        borderRadius: 14, padding: '16px 20px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all .15s', position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = color || 'var(--forest)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = urgent ? 'rgba(220,53,69,0.3)' : 'var(--border)')}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color || 'var(--forest)', borderRadius: '14px 14px 0 0' }} />
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--text)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>{sub}</div>}
    </div>
  )
}

// ── Pipeline strip ────────────────────────────────────────────────────────────
function PipelineStrip({ activeLeads, setPage }) {
  const counts = useMemo(() => {
    const map = {}
    STATUS_ORDER.forEach(s => (map[s] = 0))
    activeLeads.forEach(l => { if (map[l.lead_status] !== undefined) map[l.lead_status]++ })
    return map
  }, [activeLeads])
  const total = activeLeads.length

  return (
    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Lead Pipeline</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{total} active</span>
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setPage('leads')}>View all →</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 3, marginBottom: 14, height: 8, borderRadius: 99, overflow: 'hidden' }}>
        {STATUS_ORDER.map(status => {
          const pct = total > 0 ? (counts[status] / total) * 100 : 0
          if (pct === 0) return null
          return <div key={status} style={{ flex: pct, background: STATUS_COLORS[status], minWidth: 4 }} title={`${status}: ${counts[status]}`} />
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6 }}>
        {STATUS_ORDER.map(status => (
          <div key={status} style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setPage('leads')}>
            <div style={{ fontSize: 18, fontWeight: 700, color: counts[status] > 0 ? STATUS_COLORS[status] : 'var(--border)' }}>{counts[status]}</div>
            <div style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {status.replace('No Contact', 'NC')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Team Calendar ─────────────────────────────────────────────────────────────
function TeamCalendar({ pendingTasks, upcomingRenewals, openClient, clients, setPage }) {
  const [calendarEvents, setCalendarEvents] = useState([])
  const [addingEvent, setAddingEvent] = useState(false)
  const [newEvent, setNewEvent] = useState({ title: '', date: '', broker: '', color: 'var(--forest)' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    supabase.from('calendar_events').select('*').order('date').then(({ data }) => setCalendarEvents(data || []))
  }, [])

  const saveEvent = async () => {
    if (!newEvent.title || !newEvent.date) return
    setSaving(true)
    const { data } = await supabase.from('calendar_events').insert({
      title: newEvent.title,
      date: newEvent.date,
      broker: newEvent.broker,
      color: newEvent.color,
    }).select().single()
    if (data) setCalendarEvents(prev => [...prev, data].sort((a, b) => a.date.localeCompare(b.date)))
    setNewEvent({ title: '', date: '', broker: '', color: 'var(--forest)' })
    setAddingEvent(false)
    setSaving(false)
  }

  const deleteEvent = async (id) => {
    await supabase.from('calendar_events').delete().eq('id', id)
    setCalendarEvents(prev => prev.filter(e => e.id !== id))
  }

  // Build next 14 days
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    return d
  })

  const fmtIso = (d) => d.toISOString().split('T')[0]

  // Gather all events per day
  const eventsByDay = useMemo(() => {
    const map = {}
    days.forEach(d => { map[fmtIso(d)] = [] })

    // Renewals
    upcomingRenewals.forEach(c => {
      const iso = c.renewal_date?.split('T')[0]
      if (map[iso]) map[iso].push({ type: 'renewal', label: `🔄 ${c.company_name}`, color: '#e6a817', client: c })
    })

    // Tasks
    pendingTasks.forEach(t => {
      const iso = t.due_date?.split('T')[0]
      if (map[iso]) map[iso].push({ type: 'task', label: `✓ ${t.task_text}`, color: '#3b82f6', sub: t.client_name })
    })

    // Manual calendar events
    calendarEvents.forEach(e => {
      const iso = e.date?.split('T')[0]
      if (map[iso]) map[iso].push({ type: 'event', label: `📅 ${e.title}`, color: e.color || 'var(--forest)', id: e.id, broker: e.broker })
    })

    return map
  }, [upcomingRenewals, pendingTasks, calendarEvents])

  const EVENT_COLORS = [
    { label: 'Green', value: 'var(--forest)' },
    { label: 'Orange', value: 'var(--orange)' },
    { label: 'Blue', value: '#3b82f6' },
    { label: 'Purple', value: '#8b5cf6' },
    { label: 'Red', value: '#dc3545' },
  ]

  return (
    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>📅 Team Calendar — Next 14 Days</div>
        <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setAddingEvent(v => !v)}>
          {addingEvent ? '✕ Cancel' : '+ Add Event'}
        </button>
      </div>

      {addingEvent && (
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 2, minWidth: 160 }}>
            <label className="form-label">Event</label>
            <input className="form-input" value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Team meeting, Client call..." autoFocus />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={newEvent.date} onChange={e => setNewEvent(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label className="form-label">Broker</label>
            <select className="form-select" value={newEvent.broker} onChange={e => setNewEvent(p => ({ ...p, broker: e.target.value }))}>
              <option value="">All team</option>
              {BROKERS.map(b => <option key={b}>{b.split(' ')[0]}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 100 }}>
            <label className="form-label">Colour</label>
            <select className="form-select" value={newEvent.color} onChange={e => setNewEvent(p => ({ ...p, color: e.target.value }))}>
              {EVENT_COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={saveEvent} disabled={saving || !newEvent.title || !newEvent.date}>
            {saving ? 'Saving...' : 'Add'}
          </button>
        </div>
      )}

      <div style={{ padding: '14px 18px' }}>
        {days.map((day, di) => {
          const iso = fmtIso(day)
          const events = eventsByDay[iso] || []
          const isToday = di === 0
          const isWeekend = day.getDay() === 0 || day.getDay() === 6
          const dayLabel = isToday ? 'Today' : di === 1 ? 'Tomorrow' : day.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })

          if (events.length === 0 && !isToday) return null

          return (
            <div key={iso} style={{ display: 'flex', gap: 14, marginBottom: 12, alignItems: 'flex-start' }}>
              {/* Day label */}
              <div style={{ width: 80, flexShrink: 0, textAlign: 'right' }}>
                <div style={{
                  fontSize: 11, fontWeight: isToday ? 700 : 600,
                  color: isToday ? 'var(--forest)' : isWeekend ? 'var(--muted)' : 'var(--text)',
                  padding: '3px 8px',
                  background: isToday ? 'var(--forest-subtle)' : 'transparent',
                  borderRadius: 6,
                  border: isToday ? '1.5px solid var(--forest-border)' : 'none',
                  display: 'inline-block',
                }}>
                  {dayLabel}
                </div>
              </div>

              {/* Events */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {events.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--border)', fontStyle: 'italic', paddingTop: 3 }}>No events</div>
                ) : events.map((ev, ei) => (
                  <div key={ei} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 10px',
                    borderRadius: 7,
                    background: `${ev.color}12`,
                    border: `1px solid ${ev.color}30`,
                    cursor: ev.client ? 'pointer' : 'default',
                  }}
                    onClick={() => ev.client && openClient(ev.client)}
                  >
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: ev.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>
                      {ev.label}
                      {ev.sub && <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: 11 }}>{ev.sub}</span>}
                      {ev.broker && <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: 10 }}>· {ev.broker}</span>}
                    </div>
                    {ev.type === 'event' && (
                      <button onClick={e => { e.stopPropagation(); deleteEvent(ev.id) }} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, padding: '0 2px', opacity: 0.5 }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Hot leads ─────────────────────────────────────────────────────────────────
function HotLeads({ activeLeads, openClient }) {
  const hot = useMemo(() => {
    return activeLeads
      .filter(l => ['No Contact 1', 'No Contact 2', 'No Contact 3', 'Quote Sent', 'Awaiting Client'].includes(l.lead_status))
      .map(l => {
        const lastContact = (l.audit_log || []).filter(e => e.timestamp).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
        const staleDays = lastContact ? daysSince(lastContact.timestamp) : daysSince(l.created_at)
        return { ...l, staleDays }
      })
      .sort((a, b) => b.staleDays - a.staleDays)
      .slice(0, 8)
  }, [activeLeads])

  if (hot.length === 0) return (
    <div className="empty" style={{ padding: 24 }}>
      <div className="empty-icon">🎯</div>
      <div className="empty-title">All leads up to date</div>
    </div>
  )

  return (
    <>
      {hot.map(l => (
        <div key={l.id} onClick={() => openClient(l)} style={{ display: 'flex', alignItems: 'center', padding: '11px 18px', borderBottom: '1px solid var(--border)', cursor: 'pointer', gap: 10, transition: 'background .12s' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
          onMouseLeave={e => (e.currentTarget.style.background = '')}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[l.lead_status] || 'var(--muted)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.company_name}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>{l.lead_status} · {l.assigned_broker?.split(' ')[0]}</div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: l.staleDays >= 7 ? 'rgba(220,53,69,0.1)' : l.staleDays >= 3 ? 'rgba(230,168,23,0.1)' : 'var(--surface2)', color: l.staleDays >= 7 ? 'var(--danger)' : l.staleDays >= 3 ? '#7a5500' : 'var(--muted)', flexShrink: 0 }}>
            {l.staleDays === 0 ? 'Today' : `${l.staleDays}d`}
          </div>
        </div>
      ))}
    </>
  )
}

// ── Recent activity ───────────────────────────────────────────────────────────
function ActivityFeed({ texts, calls, openClient, clients }) {
  const feed = useMemo(() => {
    return [
      ...(texts || []).filter(t => t.direction === 'inbound').slice(0, 20).map(t => ({
        id: `t-${t.id}`, type: 'text', icon: '💬',
        title: t.client_name || t.from_number || 'Unknown',
        body: (t.body || '').substring(0, 70),
        ts: t.created_at, clientId: t.client_id,
      })),
      ...(calls || []).slice(0, 20).map(c => ({
        id: `c-${c.id}`, type: 'call', icon: c.direction === 'inbound' ? '📞' : '📲',
        title: c.client_name || c.contact_number || 'Unknown',
        body: c.duration ? `${Math.floor(c.duration / 60)}m ${c.duration % 60}s` : 'Missed call',
        ts: c.started_at, clientId: c.client_id,
      })),
    ].sort((a, b) => new Date(b.ts) - new Date(a.ts)).slice(0, 8)
  }, [texts, calls])

  if (feed.length === 0) return (
    <div className="empty" style={{ padding: 24 }}>
      <div className="empty-icon">📡</div>
      <div className="empty-title">No recent activity</div>
    </div>
  )

  return (
    <>
      {feed.map(item => {
        const client = (clients || []).find(c => c.id === item.clientId)
        return (
          <div key={item.id} onClick={() => client && openClient(client)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 18px', borderBottom: '1px solid var(--border)', cursor: client ? 'pointer' : 'default', transition: 'background .12s' }}
            onMouseEnter={e => client && (e.currentTarget.style.background = 'var(--surface2)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: item.type === 'text' ? 'rgba(59,130,246,0.1)' : 'rgba(30,61,48,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>
              {item.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 1 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.body}</div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0, marginTop: 1 }}>{fmtTimeAgo(item.ts)}</div>
          </div>
        )
      })}
    </>
  )
}

// ── Notice board ──────────────────────────────────────────────────────────────
function NoticeBoard({ user }) {
  const [notice, setNotice] = useState('')
  const [editing, setEditing] = useState(false)
  const [input, setInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [updatedBy, setUpdatedBy] = useState('')

  useEffect(() => {
    supabase.from('notices').select('*').eq('id', 1).single().then(({ data }) => {
      if (data?.content) { setNotice(data.content); setUpdatedBy(data.updated_by || '') }
    })
  }, [])

  const save = async () => {
    setSaving(true)
    await supabase.from('notices').update({ content: input, updated_at: new Date().toISOString(), updated_by: user.name }).eq('id', 1)
    setNotice(input); setUpdatedBy(user.name); setEditing(false); setSaving(false)
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderLeft: '4px solid var(--orange)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>📌</span>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Team Notice</div>
        </div>
        {!editing && (
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => { setInput(notice); setEditing(true) }}>
            {notice ? '✏️ Edit' : '+ Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div style={{ padding: '14px 18px' }}>
          <textarea className="form-input" value={input} onChange={e => setInput(e.target.value)} rows={3} placeholder="Post a notice for the team..." style={{ marginBottom: 10 }} autoFocus />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button className="btn btn-orange btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      ) : notice ? (
        <div style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{notice}</div>
          {updatedBy && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>Updated by {updatedBy}</div>}
        </div>
      ) : (
        <div style={{ padding: '18px', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>No notices — click "+ Add" to post one</div>
      )}
    </div>
  )
}

// ── MAIN DASHBOARD ────────────────────────────────────────────────────────────
export function Dashboard({
  leads, activeLeads, pendingTasks, upcomingRenewals,
  setPage, openModal, toggleTask, fmtDate, user, showToast,
  openClient, coverConfirmations, clients, texts, calls,
}) {
  const overdueTasks = pendingTasks.filter(t => t.due_date && daysUntil(t.due_date) < 0)
  const todayTasks   = pendingTasks.filter(t => t.due_date && daysUntil(t.due_date) === 0)

  return (
    <div>
      {/* ── Stat pills ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatPill
          label="Active Leads"
          value={activeLeads.length}
          sub={`${leads.filter(l => l.lead_status === 'Quote Sent').length} quotes out`}
          color="var(--orange)"
          onClick={() => setPage('leads')}
        />
        <StatPill
          label="Tasks Pending"
          value={pendingTasks.length}
          sub={overdueTasks.length > 0 ? `${overdueTasks.length} overdue` : todayTasks.length > 0 ? `${todayTasks.length} due today` : 'none urgent'}
          color={overdueTasks.length > 0 ? 'var(--danger)' : 'var(--info)'}
          urgent={overdueTasks.length > 0}
          onClick={() => setPage('tasks')}
        />
        <StatPill
          label="Renewals (60d)"
          value={upcomingRenewals.length}
          sub={`${upcomingRenewals.filter(r => r.days <= 14).length} in next 14 days`}
          color="var(--warning)"
          onClick={() => setPage('renewals')}
        />
        <StatPill
          label="Policies Placed"
          value={(coverConfirmations || []).length}
          sub={`${(coverConfirmations || []).filter(cc => {
            const w = new Date(); w.setDate(w.getDate() - w.getDay() + 1); w.setHours(0,0,0,0)
            return cc.created_at && new Date(cc.created_at) >= w
          }).length} this week`}
          color="var(--forest)"
          onClick={() => setPage('commissions')}
        />
      </div>

      {/* ── Pipeline ── */}
      <PipelineStrip activeLeads={activeLeads} setPage={setPage} />

      {/* ── Main grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        {/* Calendar — spans full left */}
        <div style={{ gridColumn: '1 / -1' }}>
          <TeamCalendar
            pendingTasks={pendingTasks}
            upcomingRenewals={upcomingRenewals}
            openClient={openClient}
            clients={clients}
            setPage={setPage}
          />
        </div>

        {/* Needs attention */}
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Needs Attention</div>
            <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setPage('leads')}>All leads →</button>
          </div>
          <HotLeads activeLeads={activeLeads} openClient={openClient} />
        </div>

        {/* Tasks */}
        <div style={{ background: 'var(--surface)', border: `1.5px solid ${overdueTasks.length > 0 ? 'rgba(220,53,69,0.25)' : 'var(--border)'}`, borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Tasks</div>
              {overdueTasks.length > 0 && <div style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 600, marginTop: 1 }}>⚠️ {overdueTasks.length} overdue</div>}
            </div>
            <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => openModal('task')}>+ Add</button>
          </div>
          {pendingTasks.length === 0 ? (
            <div className="empty" style={{ padding: 24 }}><div className="empty-icon">✅</div><div className="empty-title">All caught up</div></div>
          ) : pendingTasks.slice(0, 7).map(t => {
            const due = t.due_date ? daysUntil(t.due_date) : null
            const isOverdue = due !== null && due < 0
            const isDueToday = due === 0
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--border)', background: isOverdue ? 'rgba(220,53,69,0.02)' : '' }}>
                <div className={`task-check ${t.completed ? 'done' : ''}`} onClick={() => toggleTask(t)} style={{ marginTop: 2 }}>
                  {t.completed && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>{t.task_text}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 1 }}>
                    {t.client_name} · {t.assigned_broker?.split(' ')[0]}
                    {due !== null && (
                      <span style={{ marginLeft: 6, color: isOverdue ? 'var(--danger)' : isDueToday ? 'var(--warning)' : 'var(--muted)', fontWeight: isOverdue || isDueToday ? 600 : 400 }}>
                        {isOverdue ? `${Math.abs(due)}d overdue` : isDueToday ? 'due today' : `${due}d`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="priority-dot" style={{ background: PRIORITY_COLORS[t.priority], marginTop: 4 }} />
              </div>
            )
          })}
        </div>

        {/* Recent activity */}
        <div style={{ background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Recent Activity</div>
          </div>
          <ActivityFeed texts={texts} calls={calls} openClient={openClient} clients={clients} />
        </div>

        {/* Notice board */}
        <NoticeBoard user={user} />
      </div>
    </div>
  )
}
